import { useState } from "react";
import { NinjaArt } from "../art/NinjaArt";
import { RANKS } from "../content/ranks";
import "./PinPad.css";

export const PIN_LENGTH = 3;
export const PIN_TRIES = 3;

interface Props {
  /** Heading, e.g. "Leo, tap your three ninjas". */
  title: string;
  /** Checking: the PIN to match. Setting: omit, and the chosen PIN is returned. */
  expected?: number[] | undefined;
  onDone: (pin: number[]) => void;
  onCancel: () => void;
}

/**
 * Picture PIN: tap three ninjas in order. A sibling lock, not security. After
 * three wrong tries the child is sent to a grown-up.
 */
export function PinPad({ title, expected, onDone, onCancel }: Props) {
  const [taps, setTaps] = useState<number[]>([]);
  const [wrong, setWrong] = useState(0);
  const [shake, setShake] = useState(false);
  const locked = expected !== undefined && wrong >= PIN_TRIES;

  const tap = (i: number) => {
    if (locked || taps.length >= PIN_LENGTH) return;
    const next = [...taps, i];
    setTaps(next);
    if (next.length < PIN_LENGTH || !expected) return;
    if (next.every((x, k) => x === expected[k])) {
      onDone(next);
      return;
    }
    setWrong((w) => w + 1);
    setShake(true);
    setTimeout(() => {
      setShake(false);
      setTaps([]);
    }, 600);
  };

  return (
    <div className="pinpad">
      <h2 className="display pin-title">{title}</h2>
      <div className={"pin-dots" + (shake ? " shake" : "")} aria-live="polite">
        {Array.from({ length: PIN_LENGTH }, (_, k) => (
          <span key={k} className={"pin-dot" + (k < taps.length ? " on" : "")}>
            {!expected && taps[k] !== undefined ? (
              <NinjaArt rank={RANKS[taps[k]]!} className="pin-dot-art" />
            ) : null}
          </span>
        ))}
        <span className="visually-hidden">
          {taps.length} of {PIN_LENGTH} chosen
        </span>
      </div>
      {locked ? (
        <p className="pin-locked">Ask a grown-up to help.</p>
      ) : (
        <div className="pin-grid" role="group" aria-label="Picture PIN">
          {RANKS.map((r, i) => (
            <button
              key={r.name}
              type="button"
              className="pin-key"
              aria-label={r.name}
              onClick={() => tap(i)}
            >
              <NinjaArt rank={r} className="pin-art" />
            </button>
          ))}
        </div>
      )}
      <div className="row">
        <button type="button" className="ghost" onClick={onCancel}>
          Back
        </button>
        {!expected && (
          <>
            <button type="button" className="ghost" onClick={() => setTaps([])}>
              Start again
            </button>
            <button
              type="button"
              className="mini"
              disabled={taps.length < PIN_LENGTH}
              onClick={() => onDone(taps)}
            >
              Save this PIN
            </button>
          </>
        )}
      </div>
    </div>
  );
}
